import '@Pages/GeoMMM/Results/BusinessUnderstanding.scss';

import * as Dfns from 'date-fns/fp';

import { Fit, MAPEByGeo, SummaryMetrics } from '@blisspointmedia/bpm-types/dist/GeoMMM';
import { useCallback, useMemo, useRef, useState, FC } from 'react';
import { getGroupingValue, DateGroupingKey } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import {
  getColorForIndexedVolume,
  getThresholdBinsWithStdDevNonAdjusted
} from '@blisspointmedia/bpm-types/dist/brandHealthMetricsUtils';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { MdDone } from 'react-icons/md';
import { cptbAreaData, currencyFormatter } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { formatMoneyAsInt, formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown } from '@Components/Dropdown';
import { DropdownToggleType } from '@Components/Dropdown/dropdown-constants';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { DmaMap } from '@Components/DmaMap/DmaMap';
import {
  OUTCOME_FORMATTER_TYPE,
  OUTCOME_TYPE_STRING,
  parseSummaryNumber,
  percentageToSplitFraction
} from '@Pages/GeoMMM/DataFormatters';

import { ActualVsPredicted } from '@Pages/GeoMMM/Results/Components/ActualVsPredicted';
import { AreaChart } from '@Components/Charts/AreaChart';
import { ChartContainer } from '@Components/ChartContainer';
import { DynamicModelOverview } from '@Components/DynamicModelOverview/DynamicModelOverview';
import { ModelAccuracyGradient } from '@Pages/GeoMMM/Results/Components/ModelAccuracyGradient';
import { Spend } from '@Pages/GeoMMM/Results/Components/Spend';
import { WidgetContainer } from '@Components/WidgetContainer';
import {
  DMA_MAP_DEMO_COMPANY,
  GEO_MAP_MIN_HEIGHT,
  GEO_MAP_MIN_WIDTH,
  OTHER_GEO
} from '@Pages/GeoMMM/geo-mmm-constants';
import { Placement, Size } from '@Components/designConstants';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { Brand20 } from '../../../utils/colors';
import { downloadJSONToCSV, downloadPNG, exportToExcel } from '../../../utils/download-utils';
import { DATE_GROUPING_OPTIONS, dateToMddyy } from '../../../utils/date-utils';

type SummaryMetricsObj = Record<string, string | number>;

interface BusinessUnderstandingProps {
  groupBy: string;
  outcomeVariable: 'revenue' | 'kpiVolume';
  summaryMetricsRows: any[];
  mapeByGeoRows: any[];
  fitRows: any[];
  mediaSpendRows: any[];
  channelBreakoutMap: Record<
    string,
    {
      channel: string;
      platform: string;
      tactic: string;
      brand_nonbrand: string;
    }
  >;
  geoNames: Record<string, string>;
  lastRefreshDate: string;
}

const GEO_MAP_COLORS = ['#7FD800', '#7ed600d6', '#7ed60073', '#7ed6004d', '#7ed60033', '#7ed6001a'];

export const BusinessUnderstanding: FC<BusinessUnderstandingProps> = ({
  groupBy,
  outcomeVariable,
  summaryMetricsRows,
  mapeByGeoRows,
  fitRows,
  mediaSpendRows,
  channelBreakoutMap,
  geoNames,
  lastRefreshDate
}) => {
  const { cid: company } = useCompanyInfo();
  const [chartDateGrouping, setChartDateGrouping] = useState('Week');
  const [selectedView, setSelectedView] = useState<string>('by DMA');
  const [selectedDMA, setSelectedDMA] = useState<string>('');

  const { summaryMetricsData, mapeByGeoData, fitData, mediaSpendData } = useMemo(() => {
    if (
      summaryMetricsRows.length === 0 ||
      mapeByGeoRows.length === 0 ||
      fitRows.length === 0 ||
      mediaSpendRows.length === 0
    ) {
      return {
        summaryMetricsData: {},
        mapeByGeoData: {},
        fitData: [],
        mediaSpendData: [],
        channelBreakoutMap: {}
      };
    }

    // SUMMARY METRICS
    const summaryMetricsMap: SummaryMetricsObj = {};
    summaryMetricsRows.forEach(row => {
      summaryMetricsMap[row.metric] = row.value;
    });
    const summaryMetrics: SummaryMetrics = {
      firstModelObservation: String(summaryMetricsMap['Start Date']),
      lastModelObservation: String(summaryMetricsMap['End Date']),
      outcomeVariable: String(summaryMetricsMap.Outcome),
      averageWeeklySpend: formatMoneyAsInt(
        parseSummaryNumber(summaryMetricsMap['Average Weekly Spend'])
      ),
      lastRefreshDate,
      trainTestSplit: percentageToSplitFraction(
        parseSummaryNumber(summaryMetricsMap['Holdout Percentage'])
      ),
      wMAPE: String(parseSummaryNumber(summaryMetricsMap.wMAPE)),
      overallROAS: parseSummaryNumber(summaryMetricsMap['Total ROI']).toFixed(2)
    };

    /**
     * Filter out rows that do not have a dma name in geoNames map
     * TODO: Revisit this logic later.
     */

    // MAPE BY GEO
    const mapeByGeo: MAPEByGeo = {};
    mapeByGeoRows.forEach(row => {
      const dma = row.geo.split('_')[1];
      if (geoNames[dma]) {
        mapeByGeo[dma] = row.mape;
      }
    });

    // Meridian's 'other' bucket (every geo outside the modeled DMAs) has no place on the map but
    // carries real KPI and spend, so it stays in the national totals.
    const totalsGeo = (geo: string): string | undefined => {
      const dma = geo.split('_')[1];
      if (geoNames[dma]) return dma;
      return geo === OTHER_GEO ? OTHER_GEO : undefined;
    };

    // FIT
    const fit: Fit[] = [];
    fitRows.forEach(row => {
      const { geo, time: date, metric, expected, baseline, actual } = row;
      const dma = totalsGeo(geo);
      if (metric === 'mean' && dma) {
        const parsedDate = new Date(Date.parse(date));
        fit.push({
          geo: dma,
          date: Dfns.format('yyyy-MM-dd', parsedDate), // Temporary fix for Morgan's fake data where date is not formatted as string
          expected,
          baseline,
          actual
        });
      }
    });

    // MEDIA SPEND
    const mediaSpend: any[] = [];
    mediaSpendRows.forEach(row => {
      const { __EMPTY, geo, time: date, ...rest } = row;
      const dma = totalsGeo(geo);
      if (dma) {
        const parsedDate = new Date(Date.parse(date));
        mediaSpend.push({
          geo: dma,
          date: Dfns.format('yyyy-MM-dd', parsedDate), // Temporary fix for Morgan's fake data where date is not formatted as string
          ...rest
        });
      }
    });

    return {
      summaryMetricsData: summaryMetrics,
      mapeByGeoData: mapeByGeo,
      fitData: fit,
      mediaSpendData: mediaSpend
    };
  }, [fitRows, geoNames, lastRefreshDate, mapeByGeoRows, mediaSpendRows, summaryMetricsRows]);

  const modelOverviewDataToExport: [string[], (string | number)[]] = useMemo(() => {
    if (Object.keys(summaryMetricsData).length > 0) {
      const keyToPrettyName: {
        dataKey: string;
        name: string;
        value: string | number;
      }[] = [
        {
          dataKey: 'firstModelObservation',
          name: 'First model observation',
          value: dateToMddyy((summaryMetricsData as SummaryMetrics)?.firstModelObservation ?? '')
        },
        {
          dataKey: 'lastModelObservation',
          name: 'Last model observation',
          value: dateToMddyy((summaryMetricsData as SummaryMetrics)?.lastModelObservation ?? '')
        },
        {
          dataKey: 'averageWeeklySpend',
          name: 'Average weekly spend',
          value: (summaryMetricsData as SummaryMetrics)?.averageWeeklySpend ?? ''
        },
        {
          dataKey: 'outcomeVariable',
          name: 'Outcome variable',
          value: (summaryMetricsData as SummaryMetrics)?.outcomeVariable ?? ''
        },
        {
          dataKey: 'lastRefreshDate',
          name: 'Last refresh date',
          value: dateToMddyy((summaryMetricsData as SummaryMetrics)?.lastRefreshDate ?? '')
        },
        {
          dataKey: 'trainTestSplit',
          name: 'Train/Test split',
          value: (summaryMetricsData as SummaryMetrics)?.trainTestSplit ?? ''
        },
        {
          dataKey: 'wMAPE',
          name: 'Out-of-sample MAPE',
          value: (summaryMetricsData as SummaryMetrics)?.wMAPE ?? ''
        },
        {
          dataKey: 'overallROAS',
          name: 'Overall ROAS',
          value: (summaryMetricsData as SummaryMetrics)?.overallROAS ?? ''
        }
      ];

      return keyToPrettyName.reduce(
        (acc, cv) => {
          acc[0].push(cv.name);
          acc[1].push(cv.value);
          return acc;
        },
        [[], []] as [string[], (string | number)[]]
      );
    }
    return [[], []];
  }, [summaryMetricsData]);

  const excelDownloadModelOverview = useCallback(() => {
    exportToExcel(modelOverviewDataToExport, `${company}_model_overview`);
  }, [modelOverviewDataToExport, company]);

  const csvDownloadModelOverview = useCallback(() => {
    downloadJSONToCSV(modelOverviewDataToExport, `${company}_model_overview`);
  }, [modelOverviewDataToExport, company]);

  const actualVsPredictedData = useMemo(() => {
    const rows: {
      date: string;
      expected: number;
      actual: number;
    }[] = [];
    if (fitData) {
      const filteredData = selectedDMA ? fitData.filter(item => item.geo === selectedDMA) : fitData;

      const totalsByDate: Record<string, Record<string, number>> = {};
      for (const item of filteredData) {
        const { date, actual, expected } = item;

        const dateToUse = getGroupingValue(date, chartDateGrouping as DateGroupingKey, {});
        totalsByDate[dateToUse] = {
          actual: (totalsByDate[dateToUse]?.actual ?? 0) + actual,
          expected: (totalsByDate[dateToUse]?.expected ?? 0) + expected
        };
      }

      Object.keys(totalsByDate).forEach(date => {
        rows.push({
          date,
          actual: totalsByDate[date].actual,
          expected: totalsByDate[date].expected
        });
      });
    }

    return rows;
  }, [fitData, chartDateGrouping, selectedDMA]);

  const spendChartData = useMemo(() => {
    const rows: {
      date: string;
      spend: number;
    }[] = [];
    if (mediaSpendData && selectedView === 'by DMA') {
      const filteredData = selectedDMA
        ? mediaSpendData.filter(item => item.geo === selectedDMA)
        : mediaSpendData;

      const totalsByDate: Record<string, Record<string, number>> = {};
      for (const item of filteredData) {
        const { geo, date, ...rest } = item;

        const dateToUse = getGroupingValue(date, chartDateGrouping as DateGroupingKey, {});
        const spend = Object.values(rest).reduce<number>((sum, value) => sum + Number(value), 0);
        totalsByDate[dateToUse] = {
          spend: (totalsByDate[dateToUse]?.spend ?? 0) + spend
        };
      }

      Object.keys(totalsByDate).forEach(date => {
        rows.push({
          date,
          spend: totalsByDate[date].spend
        });
      });
    }

    return rows;
  }, [mediaSpendData, chartDateGrouping, selectedView, selectedDMA]);

  const areaChartData = useMemo(() => {
    let rows: {
      newD: any[];
      areas: any;
      groupedAreas: any;
    } = {
      newD: [],
      areas: [],
      groupedAreas: []
    };
    if (mediaSpendData && channelBreakoutMap && selectedView === 'Over Time') {
      const dataToProcess = mediaSpendData.filter(item => item.name !== 'Covariates');

      const formattedData: {
        date: string;
        value: number;
        name: string;
        channel: string;
        platform: string;
        tactic: string;
        brand_nonbrand: string;
      }[] = [];
      /** Want to normalize a wide-format dataset (one row with many breakout columns) into a long-format array,
       *  where each channel breakout becomes its own row
       */
      dataToProcess.forEach(entry => {
        const { geo, date, ...rest } = entry;
        Object.keys(rest).forEach(key => {
          const breakoutValues = channelBreakoutMap[key.toLowerCase()];
          if (breakoutValues) {
            const { channel, platform, tactic, brand_nonbrand } = breakoutValues;
            formattedData.push({
              date,
              value: rest[key],
              name: key,
              channel,
              platform,
              tactic,
              brand_nonbrand
            });
          }
        });
      });

      rows = cptbAreaData({
        fiscalCal: {},
        data: formattedData,
        data1Key: 'value',
        groupBy,
        dateGrouping: chartDateGrouping
      });
    }

    return rows;
  }, [mediaSpendData, chartDateGrouping, selectedView, channelBreakoutMap, groupBy]);

  const mapSvgRef = useRef<SVGSVGElement | null>(null);

  const { renderColor, renderToolTip } = useMemo(() => {
    if (mapeByGeoData && Object.keys(geoNames).length > 0) {
      const thresholds = getThresholdBinsWithStdDevNonAdjusted(Object.values(mapeByGeoData));

      const computeColor = (dma_code: string) => {
        const mapeValues = Object.values(mapeByGeoData);
        const minValue = Math.min(...mapeValues);
        const maxValue = Math.max(...mapeValues);
        const range = maxValue - minValue;

        // Get the MAPE value for the given DMA code
        const mape = mapeByGeoData[dma_code];

        // If no MAPE value or if all values are the same, return the first color
        if (mape === undefined || range === 0) {
          return Brand20;
        }

        return getColorForIndexedVolume(mape, thresholds, GEO_MAP_COLORS);
      };

      const computeToolTip = (dma_code: string) => {
        if (!mapeByGeoData[dma_code]) {
          return null;
        }

        const mape = mapeByGeoData[dma_code];

        return (
          <div className='geo-map-tooltip'>
            <div className='geo-map-tooltip-header'>{`${
              geoNames[dma_code] ?? 'N/A'
            } (DMA Code: ${dma_code})`}</div>
            <div className='geo-map-tooltip-body'>
              <div className='field-name'>{'MAPE '}</div>
              <div className='field-value'>{mape ? `${formatNumber(mape, 2)}%` : 'N/A'}</div>
            </div>
          </div>
        );
      };

      return {
        renderColor: computeColor,
        renderToolTip: computeToolTip
      };
    }
    return {
      renderColor: () => Brand20,
      renderToolTip: () => 'No info for this DMA'
    };
  }, [geoNames, mapeByGeoData]);

  const modelOverviewMape = parseFloat((summaryMetricsData as SummaryMetrics)?.wMAPE);
  const modelOverview = useMemo(
    () => (
      <DynamicModelOverview
        sections={[
          {
            title: 'Training Data',
            rows: [
              {
                label: 'First model observation',
                value: dateToMddyy(
                  (summaryMetricsData as SummaryMetrics)?.firstModelObservation ?? ''
                )
              },
              {
                label: 'Last model observation',
                value: dateToMddyy(
                  (summaryMetricsData as SummaryMetrics)?.lastModelObservation ?? ''
                )
              },
              {
                label: 'Average weekly spend',
                value: (summaryMetricsData as SummaryMetrics)?.averageWeeklySpend
              },
              {
                label: 'Outcome variable',
                value: (summaryMetricsData as SummaryMetrics)?.outcomeVariable
              }
            ]
          },
          {
            title: 'Recency',
            rows: [
              {
                label: 'Last refresh date',
                value: dateToMddyy((summaryMetricsData as SummaryMetrics)?.lastRefreshDate ?? '')
              }
            ]
          },
          {
            title: 'Statistics',
            rows: [
              {
                label: 'Train/Test split',
                value: (summaryMetricsData as SummaryMetrics)?.trainTestSplit,
                tooltipText:
                  'This refers to the fraction of data used to fit the model vs the fraction held out for model validation. Setting aside data for model validation is very important in ensuring that the model generalizes well to new, unseen data.'
              },
              {
                label: 'Out-of-sample MAPE',
                value: (
                  <>
                    {modelOverviewMape <= 20 && <MdDone className='green-check' size={22} />}
                    {`${formatNumber(modelOverviewMape, 2)}%`}
                  </>
                ),
                tooltipText:
                  "Mean absolute percentage error is a measure of the model's prediction accuracy, reflecting the average magnitude of the difference between actual and predicted values in percentage terms. Its definition is avg(abs((predicted - actual)/actual))."
              }
            ]
          },
          {
            title: 'Key Results',
            rows: [
              {
                label: 'Overall ROAS',
                value: (summaryMetricsData as SummaryMetrics)?.overallROAS
              }
            ]
          }
        ]}
        rightActions={
          <DownloadDropdown
            onClickOptions={[excelDownloadModelOverview, csvDownloadModelOverview]}
            menuOptions={['XLSX', 'CSV']}
          />
        }
        loading={false}
      />
    ),
    [summaryMetricsData, modelOverviewMape, excelDownloadModelOverview, csvDownloadModelOverview]
  );

  const mapeByGeoExportData = useMemo(() => {
    if (Object.keys(mapeByGeoData).length > 0 && Object.keys(geoNames).length > 0) {
      return Object.entries(mapeByGeoData).map(([dma, mape]) => ({
        dma,
        name: geoNames[dma] || 'N/A',
        mape: mape ? `${formatNumber(mape, 2)}%` : 'N/A'
      }));
    }
    return [];
  }, [mapeByGeoData, geoNames]);

  const excelDownloadModelFitByDma = useCallback(() => {
    exportToExcel(mapeByGeoExportData, `${company}_model_fit_by_dma`);
  }, [mapeByGeoExportData, company]);

  const pngDownloadModelFitByDma = useCallback(() => {
    downloadPNG('.geo-map-and-gradient', `${company}_model_fit_by_dma`);
  }, [company]);

  const chartsContent = useMemo(() => {
    const modelViewByDma = (
      <ChartContainer
        rightActions={
          <DownloadDropdown
            onClickOptions={[excelDownloadModelFitByDma, pngDownloadModelFitByDma]}
          />
        }
        title='Model Fit by DMA'
        viewRightActions
      >
        <div className='geo-map-and-gradient'>
          <div className='geo-map'>
            <AutoSizer
              renderProp={({ width }) => (
                <DmaMap
                  width={Math.max(width ?? 0, GEO_MAP_MIN_WIDTH)}
                  // Fixed height (map is authored 1100×550 + breathing room), NOT derived from the
                  // measured container height. Deriving it from offsetHeight created a feedback loop
                  // — the map's own height grew the container, which grew the measured height, which
                  // re-floored the map taller, with nothing to ever shrink it. A constant severs that
                  // loop; the width flooring above still drives horizontal scroll.
                  height={GEO_MAP_MIN_HEIGHT}
                  selection={selectedDMA}
                  onClick={dma => {
                    if (selectedDMA === dma) {
                      setSelectedDMA('');
                    } else {
                      setSelectedDMA(dma);
                    }
                  }}
                  renderColor={renderColor}
                  renderTooltip={renderToolTip}
                  ref={mapSvgRef}
                  // TODO: remove this after the demo on 10/28.
                  enableZoom={company === DMA_MAP_DEMO_COMPANY}
                />
              )}
            />
          </div>
          <div className='geo-map-and-gradient-legend-holder'>
            <ModelAccuracyGradient />
          </div>
        </div>
      </ChartContainer>
    );

    const actualVsPredictedChart = (
      <ChartContainer
        title={
          <>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              menuStrategy='fixed'
              type={DropdownToggleType.WIDGET_TITLE}
              value={chartDateGrouping}
              options={DATE_GROUPING_OPTIONS}
              onChange={option => setChartDateGrouping(option)}
            />
            <div className='header-left'>{`Historical ${OUTCOME_TYPE_STRING[outcomeVariable]}${
              selectedDMA ? ` in ${geoNames[selectedDMA]}` : ''
            }`}</div>
            <div className='header-right'>(Actual vs Predicted)</div>
          </>
        }
      >
        {actualVsPredictedData.length > 0 && (
          <ActualVsPredicted
            data={actualVsPredictedData}
            dateGrouping={chartDateGrouping}
            legendValue={OUTCOME_TYPE_STRING[outcomeVariable]}
            syncId={1}
            tooltipFormatter={val => OUTCOME_FORMATTER_TYPE[outcomeVariable].format(val, 2)}
            yAxisFormatter={val => OUTCOME_FORMATTER_TYPE[outcomeVariable].format(val, 0)}
          />
        )}
      </ChartContainer>
    );

    const spendChart = (
      <ChartContainer
        title={
          <>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              menuStrategy='fixed'
              type={DropdownToggleType.WIDGET_TITLE}
              value={chartDateGrouping}
              options={DATE_GROUPING_OPTIONS}
              onChange={option => setChartDateGrouping(option)}
            />
            <div className='header-left'>{`Historical Spend${
              selectedView === 'by DMA' && selectedDMA ? ` in ${geoNames[selectedDMA]}` : ''
            }`}</div>
          </>
        }
      >
        {spendChartData.length > 0 && selectedView === 'by DMA' && (
          <Spend
            data={spendChartData}
            dateGrouping={chartDateGrouping}
            syncId={1}
            tooltipFormatter={val => currencyFormatter.format(val, 2)}
            yAxisFormatter={val => currencyFormatter.format(val, 0)}
          />
        )}
        {Object.keys(areaChartData).length > 0 && selectedView === 'Over Time' && (
          <AreaChart
            areas={areaChartData.areas}
            data={areaChartData.newD}
            dateGrouping={chartDateGrouping}
            groupedAreas={areaChartData.groupedAreas}
            incrementality={false}
            syncId={1}
            tooltipFormatter={val =>
              val < 1000 ? currencyFormatter.format(val, 0) : currencyFormatter.format(val, 1)
            }
            xAxisDataKey='date'
            xAxisTickFormatter={val => dateToMddyy(val)}
            yAxisTickFormatter={val => currencyFormatter.format(val, 0)}
          />
        )}
      </ChartContainer>
    );

    return selectedView === 'by DMA' ? (
      <div className='by-dma-content'>
        <div className='top-row'>
          {modelViewByDma}
          <div className='right'>{modelOverview}</div>
        </div>
        {actualVsPredictedChart}
        {spendChart}
      </div>
    ) : (
      <div className='over-time-content'>
        <div>{actualVsPredictedChart}</div>
        {spendChart}
      </div>
    );
  }, [
    excelDownloadModelFitByDma,
    pngDownloadModelFitByDma,
    chartDateGrouping,
    outcomeVariable,
    selectedDMA,
    geoNames,
    actualVsPredictedData,
    selectedView,
    spendChartData,
    areaChartData,
    modelOverview,
    renderColor,
    renderToolTip,
    company
  ]);

  return (
    <div className='business-understanding-widget'>
      <WidgetContainer
        collapsible
        header={
          <div className='bu-header'>
            <span>How well does the model understand our business</span>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              className='large-title'
              onChange={selection => {
                if (selection === 'Over Time') {
                  setSelectedDMA('');
                }
                setSelectedView(selection);
              }}
              options={['by DMA', 'Over Time']}
              type={DropdownToggleType.PAGE_TITLE}
              value={selectedView}
            />
            <span>?</span>
          </div>
        }
        subHeader={
          <>
            High out-of-sample prediction accuracy is indicative of reliable causal understanding.
            <InfoTooltip rightLabel='More info' size={Size.SM}>
              The model is estimated by splitting up the time series data into slices called
              &quot;train&quot; and &quot;test.&quot; The training data is used to estimate the
              relationships between inputs (media investments, contextual variables) and outputs
              (the KPI). The test data is used to evaluate the performance of the trained model
              &quot;out of sample,&quot; i.e. on observations it has not already encountered.
              Out-of-sample predictive accuracy is a critical concept because it proxies what we
              ultimately care about, namely an understanding of likely KPI outputs given a
              combination of novel media inputs. See the out-of-sample statistics at right for
              measures of this concept. We provide these metrics at the DMA level so we can
              understand how the model fits for each geographic unit.
            </InfoTooltip>
          </>
        }
      >
        <div className='business-understanding-container'>
          <div className='left'>{chartsContent}</div>
          {selectedView === 'Over Time' && <div className='right'>{modelOverview}</div>}
        </div>
      </WidgetContainer>
    </div>
  );
};
